// ─── Central article data for the Help & Support knowledge base ───────────────
// Each article has a full markdown `content` field that ArticleReader renders
// via ReactMarkdown.  Screenshots are stored in public/assets/help/.

export const HELP_ARTICLES = [
  // ── GETTING STARTED ──────────────────────────────────────────────────────────
  {
    id: 1,
    title: 'Creating Your First Form',
    description: 'Go from zero to a published form in under five minutes using the drag-and-drop builder.',
    category: 'Getting Started',
    categoryId: 1,
    difficulty: 'Beginner',
    readTime: '5 min',
    views: 14200,
    rating: 4.9,
    lastUpdated: '2026-08-01',
    contentType: 'Guide',
    tags: ['basics', 'form-builder', 'getting-started'],
    content: `
# Creating Your First Form

## Overview

WebForms.biz gives you a drag-and-drop builder that lets you create, style, and publish a form without writing a single line of code. This guide walks you through the entire process from a blank canvas to a live link you can share.

![WebForms.biz home page — build forms that convert](/assets/help/home-page.jpg)

## Step 1 — Sign In and Open the Form Builder

1. Go to **webforms.biz** and sign in with your email and password (or via Google / Microsoft).

![Sign-in screen — enter your email and password or use a social login](/assets/help/sign-in.jpg)

2. Once on the **Dashboard**, click the **+ Create New Form** button in the top-right corner.
3. Choose **Start from Scratch** (or pick a template — covered in a separate guide).

You'll land on the Form Builder canvas. The panel on the left contains every field type you can add; the centre is your live preview canvas; and the right panel shows settings for the currently selected element.

## Step 2 — Add Fields

Drag any field from the left panel onto the canvas, or click it to append it at the bottom of the form.

Available field types include:

| Field | Best for |
|-------|----------|
| **Short Text** | Names, postcodes, single-line answers |
| **Long Text** | Comments, descriptions, open feedback |
| **Email** | Collecting validated email addresses |
| **Phone** | International phone numbers |
| **Number** | Quantities, ages, scores |
| **Dropdown / Select** | Choosing one option from a long list |
| **Radio** | Single choice from 2–5 options |
| **Checkbox** | Multiple selections |
| **Date / Time** | Appointments, deadlines |
| **File Upload** | Documents, images, attachments |
| **Signature** | Digital consent or approval |
| **Rating** | Star ratings (1–10 scale) |
| **Slider** | Numeric ranges with a visual handle |
| **Heading / Paragraph** | Static copy, instructions, section breaks |
| **Payment** | One-time payments via Stripe |

## Step 3 — Configure Each Field

Click any field on the canvas to open its settings in the right panel. Every field lets you set:

- **Label** — the question or prompt shown to the respondent
- **Placeholder** — hint text inside the input
- **Required** — whether the field must be answered before submission
- **Help Text** — a small note shown below the field

Individual field types have extra options: character limits for text fields, min/max for numbers, options for dropdowns and checkboxes, and so on.

## Step 4 — Set Up the Form Settings

Click the **Settings** tab in the right panel (or use the toolbar) to configure:

- **Form Name** — internal label, visible only to you
- **Success Message** — what respondents see after submitting
- **Redirect URL** — optional page to send respondents to after submission

## Step 5 — Publish Your Form

1. Click the **Publish** button in the top-right toolbar.
2. Toggle **Published** to on.
3. Copy the shareable link or embed code.

> **Tip:** You can preview your form at any point without publishing by clicking the **Preview** button (eye icon) in the toolbar.

## What's Next?

- [Theming your form](/knowledge-base-articles?article=4) — match your brand colours and fonts
- [Multi-step forms](/knowledge-base-articles?article=7) — break long forms into pages
- [Collecting payments](/knowledge-base-articles?article=11) — add Stripe payments

---

![The WebForms.biz Help Center — browse and search all 19 articles](/assets/help/support-tickets.jpg)
`,
  },
  {
    id: 2,
    title: 'Dashboard Overview',
    description: 'Understand every section of your dashboard and where to find key actions.',
    category: 'Getting Started',
    categoryId: 1,
    difficulty: 'Beginner',
    readTime: '4 min',
    views: 9800,
    rating: 4.7,
    lastUpdated: '2026-08-01',
    contentType: 'Guide',
    tags: ['dashboard', 'navigation', 'overview'],
    content: `
# Dashboard Overview

![The WebForms.biz app — Dashboard, Form Builder, Templates and analytics in the left sidebar](/assets/help/analytics.jpg)

## What You'll Find on the Dashboard

When you log in, the **Dashboard** is your home base. It shows a summary of all your forms and recent activity.

### Forms List

Every form you've created appears as a card showing:

- **Form name** and status (Draft / Published)
- **Submission count** — updated in real time as responses arrive
- **Last updated** date
- Quick actions: **Edit**, **Preview**, **Responses**, and **More options** (duplicate, delete, share)

### Quick Stats Bar

At the top of the dashboard you'll see totals across all your forms:

| Stat | What it means |
|------|---------------|
| Total Forms | All forms you've created (including drafts) |
| Total Responses | Combined submission count across all forms |
| This Month | Submissions received in the current calendar month |
| Avg. Completion | Average percentage of respondents who reach the Submit button |

### Sidebar Navigation

The left sidebar gives you fast access to:

- **Dashboard** — form list
- **Form Builder** — open a new or existing form
- **Responses** — view all submissions
- **Analytics** — charts and conversion metrics
- **Integrations** — connect Stripe, webhooks, and more
- **Billing** — manage your plan
- **Help & Support** — you are here

## Creating a Form from the Dashboard

Click **+ Create New Form** in the top-right of the form list. You can also browse **Templates** to start from a pre-built layout.

## Sorting and Filtering Forms

Use the search box above the form list to filter by name. The sort menu lets you order by **Last Updated**, **Name**, or **Submission Count**.
`,
  },
  {
    id: 3,
    title: 'Importing and Using Templates',
    description: 'Save time by starting from a pre-built template and customising it to fit your needs.',
    category: 'Getting Started',
    categoryId: 1,
    difficulty: 'Beginner',
    readTime: '4 min',
    views: 7600,
    rating: 4.6,
    lastUpdated: '2026-07-20',
    contentType: 'Tutorial',
    tags: ['templates', 'getting-started', 'quickstart'],
    content: `
# Importing and Using Templates

![The WebForms.biz Template Gallery — 24 professionally designed starting points](/assets/help/templates-gallery.jpg)

## Why Use a Template?

Templates give you a fully configured starting point — fields, layout, and settings already in place — so you can focus on customising rather than building from scratch.

## Browsing Templates

1. From the **Dashboard**, click **Browse Templates** or navigate to **Templates** in the sidebar.
2. Use the category filters (Contact Forms, Surveys, Orders, Events, etc.) to narrow the list.
3. Click any template card to see a live preview.

## Using a Template

1. Open the template preview and click **Use This Template**.
2. Give your new form a name and click **Create**.
3. The form opens in the builder, ready to edit.

## Customising Your Template

Everything in a template is fully editable:

- Rename, reorder, or delete any field
- Change labels, placeholders, and options
- Apply your own brand colours and fonts in the **Theme** panel
- Add or remove pages (for multi-step templates)

## Saving Your Own Template

> Coming soon — the ability to save any of your existing forms as a reusable template for your workspace.
`,
  },

  // ── FORM BUILDING ────────────────────────────────────────────────────────────
  {
    id: 4,
    title: 'Themes & Branding',
    description: 'Apply your brand colours, fonts, and styles so your forms feel native to your product.',
    category: 'Themes & Branding',
    categoryId: 9,
    difficulty: 'Beginner',
    readTime: '6 min',
    views: 11300,
    rating: 4.8,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['theme', 'branding', 'colours', 'fonts'],
    content: `
# Themes & Branding

![The WebForms.biz Form Builder — click Global Theme or Themes in the feature toolbar to style your form](/assets/help/form-builder.jpg)

## Accessing Theme Controls

In the Form Builder, look for the horizontal **feature toolbar** that sits just below the top action buttons. It contains:

- **Themes** (sparkle icon) — apply a pre-built colour scheme in one click
- **Global Theme** (palette icon) — open the full branding panel to customise colours, fonts, corner radius, and shadows The Theme panel slides open and shows all visual controls.

## Colour Settings

| Setting | What it controls |
|---------|-----------------|
| **Primary / Accent Colour** | Buttons, focus rings, and interactive highlights |
| **Background Colour** | The form card background |
| **Label Colour** | Field label text |
| **Input Background** | Background of text inputs, selects, and textareas |
| **Input Text** | Colour of text typed by respondents |
| **Footer Colour** | Background of the footer bar (multi-step forms) |

Click any colour swatch to open a hex picker. Type a hex value directly if you know your brand code.

### Resetting the Accent Colour

Click the **Reset Accent** button at the top of the colour section to revert the primary colour to the default. To restore other colours (Label, Input Background, Input Text) to their defaults, delete the hex value and leave the field empty.

## Typography

- **Font Family** — choose from a curated list of Google Fonts (Plus Jakarta Sans, Inter, DM Sans, Poppins, and more). The font loads automatically when selected.
- **Font Size** — base size for body text (affects inputs and help text)
- **Heading Style** — bold, semibold, or regular weight for field labels

## Layout

- **Form Max Width** — control how wide the form card stretches (in pixels). Useful for embedding in a narrow column.
- **Border Radius** — roundness of the card corners and input fields
- **Shadow** — card drop-shadow intensity

## Spacing Overrides

Use the **Spacing** section to fine-tune padding inside input fields and the gap between elements. Changes apply to the live form as well as the builder preview.

## Previewing Your Theme

Click the **Preview** (eye) button in the toolbar at any time. The full-screen preview shows your form exactly as a respondent will see it, including all theme settings.

## Tips

- Start with your primary brand colour as the Accent Colour — the builder will auto-suggest a matching footer colour.
- Test your form in both light and dark environments if you embed it on a dark-background site.
`,
  },
  {
    id: 5,
    title: 'Field Types Reference',
    description: 'A complete reference for every field type available in the form builder.',
    category: 'Form Building',
    categoryId: 2,
    difficulty: 'Beginner',
    readTime: '8 min',
    views: 8900,
    rating: 4.7,
    lastUpdated: '2026-08-01',
    contentType: 'Reference',
    tags: ['fields', 'reference', 'form-builder'],
    content: `
# Field Types Reference

![The WebForms.biz Form Builder — drag fields from the left Elements panel onto your form canvas](/assets/help/form-builder.jpg)

## Text Fields

### Short Text
A single-line text input. Good for names, email addresses (use the Email type for validation), postcodes, and short answers.

Options: **Min / Max length**, **Placeholder**, **Default value**

### Long Text
A multi-line textarea. Good for comments, descriptions, and open-ended feedback.

Options: **Rows** (default height), **Max length**, **Character counter**

### Email
A text input that validates the value is a correctly formatted email address before the form can submit.

### Phone
Accepts international phone numbers. Validation ensures the format is plausible (not a content validation).

### Number
A numeric input with optional min/max bounds and step size.

## Choice Fields

### Dropdown / Select
A native select element showing one option at a time. Best when you have more than 5 choices.

### Radio Buttons
Shows all options at once; respondents pick exactly one. Best for 2–5 options.

### Checkbox (Multi-select)
Respondents can tick any number of options. Stored as a comma-separated list.

## Date & Time

### Date Picker
Shows a calendar widget. Stored as ISO-8601 (YYYY-MM-DD).

### Time Picker
Stored as HH:MM (24-hour).

### Date & Time
Combined date + time picker.

## Numeric Scale Fields

### Rating (Stars)
Respondents click a number of stars. Configure **Max Stars** (default 5) and the default pre-selected value.

### Slider
A drag handle across a numeric range. Configure **Min**, **Max**, **Step**, and **Default**.

### Scale (NPS)
A numeric scale (default 1–10). Good for Net Promoter Score and satisfaction surveys.

## File & Signature

### File Upload
Respondents can drag-and-drop or browse files. Configure accepted file types and whether multiple files are allowed. Files are stored securely in private storage; you download them from the Submissions view.

### Signature
A touch-friendly canvas pad for hand-drawn signatures. Stored as a base-64 PNG.

## Payment

### Payment Field
Adds a Stripe payment step to your form. The respondent enters their card details and pays before (or as part of) submitting. See the **Collecting Payments** guide for setup.

## Display Elements

### Heading
Static bold text used as a section title. Not collected as data.

### Paragraph
Static body text. Supports line breaks. Good for instructions, disclaimers, or descriptions.

### Divider
A horizontal rule to visually separate sections.

### Image
Embed an image URL inline in the form. Good for illustrative examples or branding.
`,
  },
  {
    id: 6,
    title: 'Reordering and Organising Fields',
    description: 'Drag fields into the right order, group them, and keep your canvas tidy.',
    category: 'Form Building',
    categoryId: 2,
    difficulty: 'Beginner',
    readTime: '3 min',
    views: 5400,
    rating: 4.5,
    lastUpdated: '2026-07-15',
    contentType: 'Tutorial',
    tags: ['drag-and-drop', 'reorder', 'form-builder'],
    content: `
# Reordering and Organising Fields

![The Form Builder canvas — drag to reorder, group, and configure fields](/assets/help/form-builder.jpg)

## Dragging Fields

Hover over any field on the canvas to reveal the **drag handle** (six dots, ⠿) on the left side of the field. Click and drag the handle to move the field to a new position. A blue insertion line shows where the field will drop.

## Keyboard Reordering

Click a field to select it, then use the **up / down arrow** buttons in the field's action bar to nudge it one position at a time.

## Duplicating a Field

Select a field and click the **Copy** icon (two overlapping squares) in the action bar. The duplicate appears immediately below the original with all settings copied.

## Deleting a Field

Select a field and click the **Trash** icon, or press the **Delete** key while the field is selected. Confirm the deletion in the prompt. This action cannot be undone.

## Using Headings and Paragraphs as Section Breaks

Drop a **Heading** field between groups of related questions to create visual sections. Pair it with a **Divider** for extra separation. These display fields are skipped during data collection so they don't appear in your responses.

## Tips

- Group related fields under a shared heading — respondents are more likely to complete shorter-feeling sections.
- Put required fields near the top; optional ones at the bottom.
`,
  },

  // ── LAYOUTS ──────────────────────────────────────────────────────────────────
  {
    id: 7,
    title: 'Multi-Step Forms',
    description: 'Break a long form into pages with a progress bar, navigation buttons, and step titles.',
    category: 'Layouts',
    categoryId: 3,
    difficulty: 'Intermediate',
    readTime: '8 min',
    views: 9200,
    rating: 4.8,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['multi-step', 'pages', 'layout', 'progress'],
    content: `
# Multi-Step Forms

![The WebForms.biz Form Builder — click the Multi-Step button in the toolbar to configure form steps](/assets/help/form-builder.jpg)

## What is a Multi-Step Form?

Instead of presenting all fields at once, a multi-step form divides them across multiple pages (steps). Respondents navigate with **Previous / Next** buttons and see a progress indicator. This reduces overwhelm and often improves completion rates for longer forms.

## Enabling Multi-Step Layout

1. In the Form Builder, click the **Layout** button in the top toolbar.
2. Select **Multi-Step**.
3. A confirmation dialog appears if you already have fields — your existing fields are kept and assigned to the first step.

## Managing Steps

Open the **Multi-Step** panel in the right sidebar:

- **Add Step** — creates a new blank page
- **Rename Step** — click the step name to edit it; this appears as the step title above the fields
- **Add Description** — optional subtitle for each step
- **Reorder Steps** — drag steps up or down in the step list
- **Delete Step** — removes the step and any fields assigned to it (you'll be prompted to confirm)

## Assigning Fields to Steps

Each field card on the canvas has a **Step** selector at the top of its settings panel. Change the step number to move the field to a different page. You can also drag fields between steps directly on the canvas when the multi-step panel is open.

## Progress Bar Styles

In the multi-step settings, choose from three progress styles:

| Style | Appearance |
|-------|-----------|
| **Bar** | A filled progress bar across the top of the form |
| **Steps** | Numbered dot indicators |
| **Footer Bar** | A dark bar at the bottom of the card with Prev / Next buttons |

The **Footer Bar** style is most common for step-based surveys. Configure its background colour under **Theme → Footer Colour**.

## Navigation Button Text

Customise the Previous, Next, and Submit button labels in the multi-step settings panel. Leave blank to use the defaults (Previous / Next / Submit).

## Step Count Display

Toggle **Show step count** (e.g. "Step 2 of 4") to help respondents track their progress.

## Tips

- Keep each step to 3–6 fields. Fewer is better.
- End the first step with the easiest, least sensitive questions to build momentum.
- Use step titles to set expectations ("About You", "Your Preferences", "Review & Submit").
`,
  },
  {
    id: 8,
    title: 'Conversational Layout',
    description: 'Present one question at a time for a focused, interview-style experience.',
    category: 'Layouts',
    categoryId: 3,
    difficulty: 'Intermediate',
    readTime: '6 min',
    views: 6800,
    rating: 4.7,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['conversational', 'layout', 'one-at-a-time'],
    content: `
# Conversational Layout

![The Form Builder in Conversational mode — one question at a time](/assets/help/form-builder.jpg)

## What is Conversational Mode?

Conversational mode shows **one question at a time**, with animated transitions between each. It feels like a chat or interview, which can significantly reduce abandonment on longer forms or surveys.

## Enabling Conversational Mode

1. In the Form Builder, click **Layout** in the top toolbar.
2. Select **Conversational**.

All existing fields are preserved and will appear one by one in their current order.

## How Respondents Experience It

- Each field occupies the full screen.
- Pressing **Enter** or clicking **OK / Next** advances to the next question.
- A back arrow lets respondents review and change a previous answer.
- A **Review** screen at the end shows all answers before the final submit.
- Keyboard shortcuts: **Enter** to advance, **Backspace** on an empty input to go back, **Arrow keys** to cycle options in radio and dropdown fields.

## Auto-Advance

Each question waits for the respondent to answer before showing the next. Single-choice fields (radio, dropdown) require the respondent to confirm by pressing Enter or clicking Next.

## Field Compatibility

All standard field types work in conversational mode. File upload and payment fields appear inline on their own screen just like any other field.

## Tips

- Conversational mode works best for surveys and intake forms with 5–15 questions.
- Keep questions short and direct — one question per field.
- Avoid long checkbox lists; radio fields with 3–5 options work better.
`,
  },
  {
    id: 9,
    title: 'Split-Page Layout',
    description: 'Add a side panel with an image, video, or custom content alongside your form.',
    category: 'Layouts',
    categoryId: 3,
    difficulty: 'Intermediate',
    readTime: '5 min',
    views: 4900,
    rating: 4.6,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['split', 'layout', 'image', 'branding'],
    content: `
# Split-Page Layout

![The Form Builder in Split-Page layout — content panel on the left, form on the right](/assets/help/form-builder.jpg)

## What is the Split Layout?

The Split-Page layout divides the screen into two panels:

- **Left (or right) panel** — an image, gradient, or custom HTML content
- **Right (or left) panel** — the actual form

It's ideal for lead capture pages, event registrations, and any form where visual reinforcement of your brand helps conversions.

## Enabling Split Layout

1. In the Form Builder, click **Layout** in the top toolbar.
2. Select **Split Page**.

## Configuring the Content Panel

In the Split settings panel on the right:

- **Panel position** — choose whether the content panel is on the left or the right
- **Background type** — Solid colour, Gradient, or Image URL
- **Image URL** — paste a link to any publicly accessible image
- **Overlay opacity** — darken the image to make text more readable
- **Content** — add a headline, sub-headline, bullet points, and a logo URL

## Responsive Behaviour

On mobile screens the two panels occupy the full width; the form fields remain fully accessible. Layout behaviour on narrow viewports may vary depending on your browser and embed context.

## Tips

- Use high-contrast text on your panel — light text on a dark/image background reads best.
- Keep panel copy short: a headline, two or three bullet points, and optionally a testimonial.
- Mirror your landing page's hero image for visual continuity.
`,
  },

  // ── CONDITIONAL LOGIC ────────────────────────────────────────────────────────
  {
    id: 10,
    title: 'Conditional Logic — Show/Hide Fields',
    description: 'Make fields appear or disappear based on answers to earlier questions.',
    category: 'Logic & Conditions',
    categoryId: 4,
    difficulty: 'Intermediate',
    readTime: '9 min',
    views: 8600,
    rating: 4.7,
    lastUpdated: '2026-08-01',
    contentType: 'Guide',
    tags: ['conditional-logic', 'show-hide', 'dynamic'],
    content: `
# Conditional Logic — Show/Hide Fields

![The WebForms.biz Form Builder — click Conditional Logic in the toolbar to open the rule editor](/assets/help/form-builder.jpg)

## Why Use Conditional Logic?

Conditional logic lets you build adaptive forms that only ask relevant questions. A respondent who answers "No" to "Do you have a company?" never sees the company-name field. This reduces friction and keeps completion rates high.

## Setting Up Field Visibility

1. Click the field you want to conditionally show or hide.
2. In the field settings panel, scroll to **Visibility / Conditional Logic**.
3. Toggle **Show this field conditionally**.
4. Choose the **Source field** (the field whose answer drives this rule).
5. Choose an **Operator**: equals, not equals, contains, is empty, is not empty, greater than, etc.
6. Enter the **Value** to match.
7. Choose **Action**: **Show** (field is hidden by default, shown when condition is met) or **Hide** (field is shown by default, hidden when condition is met).

## Example: Show a Text Field Conditionally

> "If the respondent selects 'Other' in the dropdown, show a text field asking them to specify."

1. Add a **Dropdown** field with options including "Other".
2. Add a **Short Text** field labelled "Please specify".
3. On the Short Text field, set: **Source field** = the Dropdown, **Operator** = equals, **Value** = Other, **Action** = Show.

Now the text field only appears when "Other" is selected.

## Form-Level Logic Rules (Advanced)

For more complex rules — especially those affect multiple fields or whole steps — use the **Logic** panel accessible from the toolbar:

1. Click **Logic** in the toolbar.
2. Click **+ Add Rule**.
3. Set one or more **conditions** (linked by AND or OR).
4. Set the **action**: show or hide a specific field when the conditions are met.

## Operators Reference

| Operator | Matches when… |
|----------|--------------|
| equals | value is exactly the condition |
| not equals | value differs from the condition |
| contains | value includes the condition string |
| is empty | field has no answer |
| is not empty | field has any answer |
| greater than / less than | numeric comparison |

## Important Notes

- Test your logic using the **Preview** button (not just the builder canvas) before publishing — the canvas shows a simplified view that may not fully reflect all rules.
- **Required hidden fields:** when a field is hidden by a logic rule, respondents are not expected to fill it in. Verify this behaviour in Preview mode before going live.
`,
  },

  // ── INTEGRATIONS ─────────────────────────────────────────────────────────────
  {
    id: 11,
    title: 'Collecting Payments with Stripe',
    description: 'Add a Stripe payment field to your form to accept one-time payments or donations.',
    category: 'Integrations',
    categoryId: 5,
    difficulty: 'Intermediate',
    readTime: '8 min',
    views: 10100,
    rating: 4.8,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['stripe', 'payments', 'integration'],
    content: `
# Collecting Payments with Stripe

![Stripe payment field in the Form Builder — collect card payments on your form](/assets/help/form-builder.jpg)

## Prerequisites

- A Stripe account (free to create at stripe.com)
- Your Stripe **Publishable Key** (found in Stripe Dashboard → Developers → API Keys)
- For live payments: a verified Stripe account

## Step 1 — Connect Stripe

1. Go to **Integrations** in the sidebar.
2. Click **Stripe** → **Connect**.
3. Enter your Stripe Publishable Key (starts with \`pk_test_\` for test mode or \`pk_live_\` for live).
4. Click **Save**.

## Step 2 — Add a Payment Field to Your Form

1. Open the Form Builder and drag a **Payment** field onto the canvas.
2. In the field settings, enter:
   - **Amount** — fixed amount in your currency, or leave dynamic for the respondent to enter
   - **Currency** — select your currency (USD, GBP, EUR, etc.)
   - **Description** — shown on the Stripe payment page and receipt

## Step 3 — Test Your Payment Form

1. Set your Stripe account to **Test Mode** (the toggle in the Stripe Dashboard top bar).
2. Preview your form and complete a payment using Stripe's test card: **4242 4242 4242 4242**, any future expiry, any CVV.
3. Check the Stripe Dashboard → Payments to confirm the test charge appeared.

## Step 4 — Go Live

1. In your Stripe Dashboard, complete account verification.
2. Switch to live mode and copy your live Publishable Key (\`pk_live_…\`).
3. Update the key in **Integrations → Stripe**.

## How Payments Work on Submission

When a respondent reaches the payment field they enter their card details using Stripe's secure card element. The form will not submit until payment is authorised. You'll see the payment in your Stripe Dashboard immediately.

> **Note:** WebForms.biz never stores or sees card numbers. All card data is handled directly by Stripe's PCI-compliant infrastructure.

## Refunds

Issue refunds directly from your Stripe Dashboard → Payments → find the charge → Refund.
`,
  },
  {
    id: 12,
    title: 'Managing Submissions',
    description: 'Review, organize, and follow up on form responses from one dashboard.',
    category: 'Getting Started',
    categoryId: 1,
    difficulty: 'Beginner',
    readTime: '5 min',
    views: 8400,
    rating: 4.7,
    lastUpdated: '2026-08-01',
    contentType: 'Tutorial',
    tags: ['submissions', 'responses', 'follow-up'],
    content: `
# Managing Submissions

![The WebForms.biz Submissions dashboard](/assets/help/form-builder.jpg)

## Where Responses Appear

Every completed response is saved in your **Submissions** dashboard. Use it as the central place to review answers and decide how you want to follow up.

## Reviewing Responses

1. Open **Submissions** from the main navigation.
2. Select a response to view every answer and uploaded file.
3. Use filters and labels to organize work by form, date, or status.
4. Export responses when you need to work in a spreadsheet or another tool.
5. Follow up with respondents through the channel that works best for your team.

## Keeping Work Organized

- Add labels to highlight urgent, qualified, or completed responses.
- Use the submission detail view to keep all form answers together while you follow up manually.
- Connect a webhook when you need to send a submission to another system in real time.
`,
  },
  {
    id: 13,
    title: 'Webhooks',
    description: 'Send submission data to any URL in real time when a form is submitted.',
    category: 'Integrations',
    categoryId: 5,
    difficulty: 'Advanced',
    readTime: '7 min',
    views: 5200,
    rating: 4.6,
    lastUpdated: '2026-07-20',
    contentType: 'Guide',
    tags: ['webhook', 'api', 'integration'],
    content: `
# Webhooks

![The WebForms.biz Form Builder — select a field and open the Webhooks tab in the field Settings panel](/assets/help/form-builder.jpg)

## What is a Webhook?

A webhook is an HTTP POST request sent automatically to a URL you specify whenever your form receives a submission. Use webhooks to:

- Push responses into a CRM (HubSpot, Salesforce)
- Send data to Zapier / Make
- Update a spreadsheet
- Notify a Slack channel
- Feed data into your own backend

## Adding a Webhook

1. In the Form Builder, click the **Settings** tab in the right panel.
2. Select the **Webhooks** tab.
3. Click **+ Add Webhook**.
4. Enter your **Endpoint URL** and click **Save**.

## Payload Format

Every webhook call is a POST request with \`Content-Type: application/json\`:

\`\`\`json
{
  "event": "form.submitted",
  "formId": "abc123",
  "formName": "Contact Us",
  "submissionId": "sub_xyz",
  "submittedAt": "2026-08-05T14:32:00Z",
  "submissionData": {
    "Name": "Jane Smith",
    "Email": "jane@example.com",
    "Message": "Hello!"
  }
}
\`\`\`

## Testing Your Webhook

Use a service like [webhook.site](https://webhook.site) to get a temporary endpoint and inspect the payload before pointing it at your real system. Submit a test response and check the request body in webhook.site's inspector.

## Security

Webhook calls include a \`User-Agent: WebForms.biz-Webhook/1.0\` header. We recommend validating requests on your server using a secret token — support for request signing (HMAC) is on the roadmap.

## Retries

If your endpoint returns a non-2xx status code, the webhook is logged as failed. You can inspect the endpoint and submit a new test response after correcting the issue.
`,
  },
  {
    id: 14,
    title: 'Embedding Forms on Your Website',
    description: 'Add your form to any website using an iframe, JavaScript snippet, or popup.',
    category: 'Integrations',
    categoryId: 5,
    difficulty: 'Beginner',
    readTime: '6 min',
    views: 7800,
    rating: 4.9,
    lastUpdated: '2026-08-01',
    contentType: 'Tutorial',
    tags: ['embed', 'iframe', 'website', 'publish'],
    content: `
# Embedding Forms on Your Website

![WebForms.biz Embed Settings — JavaScript, iFrame, HTML, React, and WordPress embed methods](/assets/help/embed-settings.jpg)

## How to Get Your Embed Code

There are two ways to access embed options:

**From the Dashboard:**
1. Find your form card and click the **Share** icon (share arrow) to open the Share dialog.
2. The dialog shows a **Direct Link**, an **iFrame** snippet, and a **Script** snippet. Click **Copy** next to the method you want.

**From the Form Builder (after publishing):**
1. Click **Publish** in the top-right of the builder to save and navigate to the Publish screen.
2. On the Publish screen, click the **Embed** tab.
3. Choose between **JavaScript**, **iFrame**, **HTML**, **React**, or **WordPress** methods and copy the snippet.

For advanced control, go to the **Embed Settings** page (linked from the Publish screen) to configure auto-resize, CSS isolation, lazy loading, and display position.

## Method 1: Direct Link

Share the URL directly — no code needed. Respondents open your form in a standalone page. Best for email campaigns, social media, and QR codes.

## Method 2: iFrame Embed

Copy the iFrame snippet from the Share dialog or Embed tab and paste it into your page HTML:

\`\`\`html
<iframe
  src="https://YOUR_FORM_URL"
  width="100%"
  height="600"
  frameborder="0"
  style="border:none;border-radius:8px;"
></iframe>
\`\`\`

## Method 3: JavaScript Embed

Use the JavaScript method (from Embed Settings) for auto-resizing and event callbacks. This is the recommended choice for dynamic pages.

## Tips

- Publish your form before sharing or embedding — unpublished forms show a notice to visitors.
- Always test the embed on your actual website after publishing changes.
- Use **Embed Settings** for advanced options including CSS isolation, lazy loading, and inline vs popup display.
`,
  },

  // ── ANALYTICS ────────────────────────────────────────────────────────────────
  {
    id: 15,
    title: 'Understanding Your Analytics',
    description: 'Read your form\'s performance metrics, spot drop-off points, and improve completion rates.',
    category: 'Analytics & Reporting',
    categoryId: 6,
    difficulty: 'Intermediate',
    readTime: '9 min',
    views: 6300,
    rating: 4.6,
    lastUpdated: '2026-08-01',
    contentType: 'Guide',
    tags: ['analytics', 'metrics', 'conversion', 'reporting'],
    content: `
# Understanding Your Analytics

![Analytics — form performance, response trends, and recent activity](/assets/help/analytics.jpg)

## Opening Analytics

Click **Analytics** in the left sidebar. It opens with an account-wide overview, so you can immediately see form performance without first choosing a form.

## Analytics Tabs

- **Overview** — compare response volume and top-performing forms across your account.
- **By form** — choose a specific form, or keep **All forms** selected, to narrow down the response metrics.
- **Activity** — review recently recorded responses in one feed.

## Key Metrics

| Metric | Definition |
|--------|-----------|
| **Views** | Times the form URL was loaded |
| **Starts** | Respondents who interacted with at least one field |
| **Submissions** | Completed, successful form submits |
| **Completion Rate** | Submissions ÷ Starts × 100 |
| **Avg. Time to Complete** | Median time from first field interaction to submit |

## The Submissions Chart

The main chart shows submissions over time, grouped by day. Hover over any data point for the exact count on that day.

## Field-level Drop-off (Coming Soon)

The field-level drop-off view will show at which question respondents abandon the form most frequently. Use this to identify confusing, sensitive, or unnecessarily complex fields.

## Improving Completion Rate

Common causes of low completion rates and how to address them:

1. **Too many fields** — Remove anything not strictly needed. Every extra field costs completions.
2. **A required field respondents can't answer** — Make it optional or add a "Not applicable" option.
3. **Long forms without progress feedback** — Switch to Multi-Step layout with a progress bar.
4. **Mobile usability issues** — Preview on a narrow screen and check field tap targets.
5. **Slow load time** — Reduce the number of images and embedded media.

## Viewing Submissions

Go to **Responses** in the sidebar to browse every submission in detail. You can open any submission to see individual field answers, and delete submissions you no longer need.
`,
  },
  {
    id: 16,
    title: 'Viewing and Managing Responses',
    description: 'Browse, search, filter, and manage the submissions your forms receive.',
    category: 'Analytics & Reporting',
    categoryId: 6,
    difficulty: 'Beginner',
    readTime: '5 min',
    views: 9700,
    rating: 4.8,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['responses', 'submissions', 'manage'],
    content: `
# Viewing and Managing Responses

![The Responses view — browse, filter, and manage all form submissions](/assets/help/responses.jpg)

## Opening the Responses View

1. From the Dashboard, click **Responses** next to a form, or use the **Responses** link in the sidebar.
2. The table shows every submission, newest first.

## The Response Table

Each row shows:
- **Submission ID** — a unique reference
- **Submitted At** — date and time (your local timezone)
- **Status** — New, Reviewed, Archived, or Flagged
- Key field values — the first few answers shown as column previews

Click any row to open the full detail view showing every field and answer.

## Searching and Filtering

- **Search box** — filters rows by any visible text (names, emails, etc.)
- **Status filter** — Completed or Partial (partial = respondent started but didn't submit)

## Deleting a Submission

Open the submission detail view and click **Delete Submission** (trash icon) in the top-right. Confirm the prompt. This action is permanent.

> **Note:** Deleting a submission also removes any uploaded files associated with it from storage.

## Refreshing Results

Click **Refresh** or reload the page to see the latest submissions. New submissions are saved in real time on the server; refreshing the page always shows the current state.
`,
  },

  // ── ACCOUNT & BILLING ────────────────────────────────────────────────────────
  {
    id: 17,
    title: 'Plans, Limits, and Upgrades',
    description: 'Understand what each plan includes and how to upgrade when you need more.',
    category: 'Account & Billing',
    categoryId: 7,
    difficulty: 'Beginner',
    readTime: '5 min',
    views: 7100,
    rating: 4.5,
    lastUpdated: '2026-08-05',
    contentType: 'Reference',
    tags: ['billing', 'plans', 'upgrade', 'limits'],
    content: `
# Plans, Limits, and Upgrades

![WebForms.biz Account Settings — click Billing to view your current plan and usage](/assets/help/account-settings.jpg)

## Plan Overview

WebForms.biz offers tiered plans that scale with your usage. Each plan defines limits on:

| Limit | What it controls |
|-------|-----------------|
| **Forms** | Maximum number of forms you can create |
| **Submissions / month** | Responses accepted across all forms per month |
| **Features** | Which advanced features are unlocked (analytics, integrations, etc.) |

## Checking Your Current Usage

1. Click your avatar in the top-right corner → **Account Settings**.
2. Go to the **Billing** tab.
3. The **Usage** section shows how much of each limit you've consumed this billing period.

## What Happens at the Limit

- **Form limit reached** — you can't create a new form. Existing forms continue to work and collect responses. Upgrade to unlock more forms.
- **Submission limit reached** — new submissions to your forms will be declined with a polite message. Existing data is safe.

## Upgrading Your Plan

1. Go to **Account Settings → Billing**.
2. Click **Upgrade Plan**.
3. Choose the plan you want and confirm payment.

Your new limits take effect immediately. Any usage already counted this period carries over.

## Downgrading

Contact support to downgrade. Downgrading takes effect at the end of the current billing period. You'll keep access to all current features until then.

## Cancelling

1. Go to **Account Settings → Billing → Cancel Plan**.
2. Your account reverts to the free tier at the end of the current period. Your data is retained.
`,
  },
  {
    id: 18,
    title: 'Account Settings and Profile',
    description: 'Update your name, email, password, and notification preferences.',
    category: 'Account & Billing',
    categoryId: 7,
    difficulty: 'Beginner',
    readTime: '3 min',
    views: 5600,
    rating: 4.4,
    lastUpdated: '2026-07-20',
    contentType: 'Guide',
    tags: ['account', 'profile', 'password', 'settings'],
    content: `
# Account Settings and Profile

![WebForms.biz Account Settings — manage your profile, billing, team, integrations, and security](/assets/help/account-settings.jpg)

## Opening Account Settings

Click your **avatar** (or initials) in the top-right corner of the navigation bar → **Account Settings**.

## Profile Tab

- **First Name / Last Name** — shown in the UI and on notification emails
- **Email Address** — your sign-in email (used for login and notifications)
- **Profile Photo** — click the camera icon over the avatar circle to upload a JPG, PNG, or GIF (max 5 MB)
- **Company** and **Job Title** — optional organisation details
- **Phone Number** — optional

Click **Save Changes** after editing.

## Security Tab

- **Change Password** — enter your current password, then your new password twice. Passwords must be at least 8 characters.

## Account Preferences

Control account-level preferences:

- **Product updates** — new features and release notes
- **Billing reminders** — invoices and renewal reminders

## Closing or Deleting Your Account

To close your account or request data deletion, contact support via **Help & Support → Submit Ticket**. The support team will process your request and confirm the outcome by email.
`,
  },

  // ── TROUBLESHOOTING ──────────────────────────────────────────────────────────
  {
    id: 19,
    title: 'Troubleshooting Common Issues',
    description: 'Solutions to the most frequently encountered problems with forms and submissions.',
    category: 'Troubleshooting',
    categoryId: 8,
    difficulty: 'Beginner',
    readTime: '7 min',
    views: 11500,
    rating: 4.6,
    lastUpdated: '2026-08-05',
    contentType: 'Guide',
    tags: ['troubleshooting', 'errors', 'fixes', 'help'],
    content: `
# Troubleshooting Common Issues

![Sign in to WebForms.biz — if you're experiencing issues, check the steps below](/assets/help/support-tickets.jpg)

## My form won't submit

**Check 1: Required fields**
All fields marked **Required** must have a value. The form highlights empty required fields in red when you try to submit.

**Check 2: Email format**
If you have an Email field, the value must be a valid email address format (e.g. user@example.com).

**Check 3: Payment not completed**
If your form includes a Payment field, the card payment must succeed before the form submits. Verify the card details and check that Stripe is in the correct mode (test vs. live).

**Check 4: File size limit**
File upload fields accept up to 10 MB per file by default. Larger files are rejected with an error message.

---

## Submissions are not appearing in my Responses view

1. Refresh the page — a forced refresh guarantees you see the latest data.
2. Check that the form is **Published** (unpublished forms block new submissions).
3. Check your status filter — if "Partial" is hidden you might be missing incomplete submissions.

---

## Conditional logic is not working as expected

1. Open the form in **Preview** mode (not the builder canvas) — the canvas shows a simplified preview that may not reflect all logic rules.
2. Check the **source field** ID — if you renamed a field after creating the rule, re-select it.
3. Verify the **operator and value** match exactly what the respondent would type or select.

---

## My embedded form shows a blank page

1. Check that the form is **Published**.
2. Ensure the iframe URL is correct and hasn't been modified.
3. Some browser extensions (ad blockers) can block iframes. Test in an incognito window.
4. Check your browser's console for CORS errors — contact support if you see them.

---

## The form builder is not saving my changes

1. Check your internet connection.
2. Try refreshing — the builder auto-saves; if the page shows a yellow "Unsaved changes" badge, click **Save** manually.
3. Clear your browser cache and try again.
4. If the problem persists, copy your form's URL, open it in a new tab, and check if the latest version is shown.
`,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

export const ARTICLE_CATEGORIES = [
  { id: 1, name: 'Getting Started',       icon: 'Rocket',       articles: HELP_ARTICLES.filter(a => a.categoryId === 1) },
  { id: 2, name: 'Form Building',         icon: 'Layout',       articles: HELP_ARTICLES.filter(a => a.categoryId === 2) },
  { id: 9, name: 'Themes & Branding',     icon: 'Palette',      articles: HELP_ARTICLES.filter(a => a.categoryId === 9) },
  { id: 3, name: 'Layouts',               icon: 'Layers',       articles: HELP_ARTICLES.filter(a => a.categoryId === 3) },
  { id: 4, name: 'Logic & Conditions',    icon: 'GitBranch',    articles: HELP_ARTICLES.filter(a => a.categoryId === 4) },
  { id: 5, name: 'Integrations',          icon: 'Plug',         articles: HELP_ARTICLES.filter(a => a.categoryId === 5) },
  { id: 6, name: 'Analytics & Reporting', icon: 'BarChart3',    articles: HELP_ARTICLES.filter(a => a.categoryId === 6) },
  { id: 7, name: 'Account & Billing',     icon: 'CreditCard',   articles: HELP_ARTICLES.filter(a => a.categoryId === 7) },
  { id: 8, name: 'Troubleshooting',       icon: 'AlertCircle',  articles: HELP_ARTICLES.filter(a => a.categoryId === 8) },
];

export const getArticleById = (id) => HELP_ARTICLES.find(a => a.id === Number(id));

export const searchArticles = (query) => {
  if (!query?.trim()) return HELP_ARTICLES;
  const q = query.toLowerCase();
  return HELP_ARTICLES.filter(a =>
    a.title.toLowerCase().includes(q) ||
    a.description.toLowerCase().includes(q) ||
    a.tags?.some(t => t.includes(q)) ||
    a.category.toLowerCase().includes(q)
  );
};
